import { cn } from '../../../lib/utils';
import { useTranslation } from 'react-i18next';
import { Platform } from '../../../types/Platforms';
import { PlatformStatistic } from '../../../utils/priceAlertUtils';
import { SupplierLogo } from '../../ui/SupplierLogo';
import {
  baseTextSize,
  borderGrey,
  extraSmallTextSize,
  mediumText,
  primaryText,
  regularText,
  quaternaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  whiteBG,
} from '../../../styling/stylingHelper';
import { CardWithHeader } from '../../ui/CardWithHeader';

export interface PlatformStatisticsProps {
  statistics: PlatformStatistic[];
}

export const PlatformStatistics = ({ statistics }: PlatformStatisticsProps) => {
  const { t } = useTranslation();

  return (
    <CardWithHeader title={t('priceAlerts.platformStatistics')}>
      {statistics.length === 0 ? (
        // Use absolute centering for empty state
        <div className="relative min-h-[180px] w-full">
          <div className={cn(
            baseTextSize,
            tertiaryText,
            "absolute inset-0 flex items-center justify-center"
          )}>
            <span className="text-center w-full">
              {t('priceAlerts.noPlatformStatistics')}
            </span>
          </div>
        </div>
      ) : (
        // Grid of platform cards, up to 3 columns, aligned left, full width
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          {statistics.map((stat) => (
            <div
              key={stat.platform}
              className={cn(
                'rounded-xl p-4 border',
                borderGrey,
                whiteBG
              )}
            >
              {/* Platform header with logo, name, and total alerts on top right */}
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-borderGrey dark:border-borderGrey-dark">
                {/* Left side: logo and name */}
                <div className="flex items-center gap-3 min-w-0">
                  <SupplierLogo
                    platform={stat.platform as Platform}
                    size="large"
                    alt={stat.platform}
                  />
                  <h4 className={cn(smallTextSize, mediumText, primaryText, "truncate")}>
                    {stat.platform}
                  </h4>
                </div>
                {/* Right side: Total Alerts */}
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className={cn(extraSmallTextSize, quaternaryText, regularText)}>
                    {t('priceAlerts.totalLabel')}
                  </span>
                  <span className={cn(extraSmallTextSize, mediumText, primaryText)}>
                    {stat.totalAlerts}
                  </span>
                </div>
              </div>

              {/* Statistics */}
              <div className="space-y-2">
                {/* Price Increases */}
                <div className="flex justify-between items-center">
                  <span className={cn(smallTextSize, quaternaryText, regularText)}>
                    {t('priceAlerts.increases')}
                  </span>
                  <span className={cn(
                    smallTextSize,
                    semiBoldText,
                    'text-[#D92D20] dark:text-textRed-dark'
                  )}>
                    {stat.priceIncreases}
                  </span>
                </div>

                {/* Price Decreases */}
                <div className="flex justify-between items-center">
                  <span className={cn(smallTextSize, quaternaryText, regularText)}>
                    {t('priceAlerts.decreases')}
                  </span>
                  <span className={cn(
                    extraSmallTextSize,
                    semiBoldText,
                    'text-[#069A6E] dark:text-darkGreenColor-dark'
                  )}>
                    {stat.priceDecreases}
                  </span>
                </div>

                {/* Average Change */}
                <div className="flex justify-between items-center">
                  <span className={cn(smallTextSize, quaternaryText, regularText)}>
                    {t('priceAlerts.avgChange')}
                  </span>
                  <span
                    className={cn(
                      smallTextSize,
                      semiBoldText,
                      stat.avgPriceChange > 0
                        ? 'text-[#D92D20] dark:text-textRed-dark'
                        : stat.avgPriceChange < 0
                        ? 'text-[#069A6E] dark:text-darkGreenColor-dark'
                        : primaryText
                    )}
                  >
                    {stat.avgPriceChange > 0 ? '+' : ''}
                    {stat.avgPriceChange.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardWithHeader>
  );
};
